import { ButtonNew as Button } from "components/buttonNew";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faPlus, faRotate, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { IWebsiteBuilderSlideshowImage, WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { findLastIndex, range } from "lodash";
import { WebsiteBuilderHelpIcon } from "pages/secure/facility/websiteBuilder/editor/components/helpIcon/WebsiteBuilderHelpIcon";
import { getEmptyImage } from "react-dnd-html5-backend";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_DRAG_LAYER } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WEBSITE_BUILDER_Z_LAYERS } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderZLayers";

interface ISlideshowImagesManageViewProps {
  images: IWebsiteBuilderSlideshowImage[];
  setImages: React.Dispatch<React.SetStateAction<IWebsiteBuilderSlideshowImage[]>>;
  navigateToAdd: () => void;
  navigateToReplace: (index: number, image: IWebsiteBuilderSlideshowImage) => void;
}

export function SlideshowImagesManageView(props: ISlideshowImagesManageViewProps) {
  const { images, setImages, navigateToAdd, navigateToReplace } = props;

  const { t } = useTranslation();

  const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);
  const [hoveredMediaId, setHoveredMediaId] = useState<number>(null);

  const imagesExist = images?.length > 0;
  const allImagesSelected = selectedMediaIds.length === images.length;

  function removeImage(index: number, mediaId: number) {
    setImages(prev => [
      ...prev.slice(0, index),
      ...prev.slice(index + 1),
    ]);

    setSelectedMediaIds(prev => prev.filter(selectedMediaId => selectedMediaId !== mediaId));
  }

  function selectImage(event: React.MouseEvent<HTMLDivElement>, index: number, mediaId: number) {
    if (event.shiftKey) {
      if (selectedMediaIds.length === 0) {
        return;
      }

      const selectedMediaItems = selectedMediaIds.map(selectedMediaId => ({
        mediaId: selectedMediaId,
        index: images.findIndex(image => image.mediaId === selectedMediaId),
      }));

      if (selectedMediaItems.some(selectedMediaItem => selectedMediaItem.index === -1 || selectedMediaItem.index === index)) {
        return;
      }

      const leftmostSelectedIndex = Math.min(...selectedMediaItems.map(selectedMediaItem => selectedMediaItem.index));

      if (index < leftmostSelectedIndex) {
        return;
      }

      const rangeStartSelectedMediaItemIndex = findLastIndex(
        selectedMediaItems,
        selectedMediaItem => selectedMediaItem.index < index,
      );

      const rangeStartIndex = selectedMediaItems[rangeStartSelectedMediaItemIndex].index;

      const intermediateSelectedMediaItems = range(rangeStartIndex + 1, index + 1).map(intermediateIndex => ({
        mediaId: images[intermediateIndex]?.mediaId,
        index: intermediateIndex,
      }));

      if (intermediateSelectedMediaItems.some(selectedMediaItem => selectedMediaItem.mediaId == null)) {
        return;
      }

      const updatedSelectedMediaItems = [
        ...selectedMediaItems.slice(0, rangeStartSelectedMediaItemIndex + 1),
        ...intermediateSelectedMediaItems,
        ...selectedMediaItems.slice(rangeStartSelectedMediaItemIndex + 1),
      ];

      const nextSelectedMediaIds = updatedSelectedMediaItems.map(selectedMediaItem => selectedMediaItem.mediaId);

      setSelectedMediaIds(nextSelectedMediaIds);
    } else {
      const nextSelectedMediaIds = selectedMediaIds.includes(mediaId)
        ? selectedMediaIds.filter(selectedMediaId => selectedMediaId !== mediaId)
        : [...selectedMediaIds, mediaId];
  
      setSelectedMediaIds(nextSelectedMediaIds);
    }
  }

  function selectAllImages() {
    setSelectedMediaIds(images.map(image => image.mediaId));
  }

  function deselectAllImages() {
    setSelectedMediaIds([]);
  }

  function removeSelectedImages() {
    setImages(prev => prev.filter(image =>
      !selectedMediaIds.includes(image.mediaId),
    ));

    deselectAllImages();
  }

  function swapImages(firstIndex: number, secondIndex: number) {
    setImages(prev => {
      const nextImages = [...prev];

      const temp = nextImages[firstIndex];

      nextImages[firstIndex] = nextImages[secondIndex];
      nextImages[secondIndex] = temp;

      return nextImages;
    });
  }

  function renderImages() {
    if (imagesExist) {
      return (
        <div className="slideshow-images-manage-grid">
          {images.map((image, index) => (
            <ManageViewSlot
              key={image.mediaId}
              index={index}
              swapImages={swapImages}
              setHoveredMediaId={setHoveredMediaId}
            >
              <ManageViewItem
                image={image}
                index={index}
                hovered={hoveredMediaId === image.mediaId}
                selected={selectedMediaIds.includes(image.mediaId)}
                setHoveredMediaId={setHoveredMediaId}
                selectImage={event => selectImage(event, index, image.mediaId)}
                removeImage={() => removeImage(index, image.mediaId)}
                replaceImage={() => navigateToReplace(index, image)}
              />
            </ManageViewSlot>
          ))}
        </div>
      );
    } else {
      return (
        <div className="slideshow-images-manage-empty">
          <div className="slideshow-images-manage-empty-message">
            <div className="slideshow-images-manage-empty-message-primary">{t("No images added yet")}</div>
            <div className="slideshow-images-manage-empty-message-supporting">{t("Add images to continue building your slideshow.")}</div>
          </div>
          <Button
            type="primary"
            onClick={navigateToAdd}
          >
            <FontAwesomeIcon icon={faPlus} />&nbsp;{t("Add images")}
          </Button>
        </div>
      );
    }
  }

  return (
    <div
      className={classNames("website-builder-slideshow-images-manage", {
        "images-exist": imagesExist,
      })}
    >
      {imagesExist && (
        <div className="slideshow-images-manage-header">
          <Button
            type="secondary"
            onClick={navigateToAdd}
          >
            <FontAwesomeIcon icon={faPlus} />&nbsp;{t("Add images")}
          </Button>
          {selectedMediaIds.length === 0 ? (
            <div className="slideshow-images-manage-total">
              <div>
                {t("{{imageCount}} of {{maxImages}} images", {
                  imageCount: images.length,
                  maxImages: WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES,
                })}
              </div>
              <WebsiteBuilderHelpIcon
                id="website-builder-slideshow-images-manage-total-help"
                message={t("You have used {{imageCount}} out of {{maxImages}} images.", {
                  imageCount: images.length,
                  maxImages: WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES,
                })}
                placement="top"
              />
            </div>
          ) : (
            <div className="slideshow-images-manage-selection">
              <div className="slideshow-images-manage-selection-message">
                {t("{{selectedCount}} selected", { selectedCount: selectedMediaIds.length })}
              </div>
              <div className="slideshow-images-manage-selection-divider" />
              <button
                type="button"
                onClick={selectAllImages}
                disabled={allImagesSelected}
              >
                {t("Select all")}
              </button>
              <div className="slideshow-images-manage-selection-divider" />
              <button
                type="button"
                onClick={deselectAllImages}
                disabled={selectedMediaIds.length === 0}
              >
                {t("Deselect all")}
              </button>
              <div className="slideshow-images-manage-selection-divider" />
              <button
                type="button"
                className="slideshow-images-manage-selection-remove"
                onClick={removeSelectedImages}
                disabled={selectedMediaIds.length === 0}
              >
                {t("Remove")}
              </button>
            </div>
          )}
        </div>
      )}
      {renderImages()}
      <ManageViewDragLayer />
    </div>
  );
}

interface IManageViewSlotProps {
  children: ReactNode;
  index: number;
  swapImages: (firstIndex: number, secondIndex: number) => void;
  setHoveredMediaId: React.Dispatch<React.SetStateAction<number>>;
}

interface IManageViewSlotDropState {
  highlight: boolean;
}

function ManageViewSlot(props: IManageViewSlotProps) {
  const { children, index, swapImages, setHoveredMediaId } = props;

  const [dropState, drop] = useDrop<IManageViewItemDragItem, unknown, IManageViewSlotDropState>(
    () => ({
      accept: ACCEPTED_MANAGE_VIEW_ITEM_DRAG_ITEM,
      drop: item => {
        swapImages(item.index, index);
        setHoveredMediaId(item.image.mediaId);
      },
      canDrop: item => item.index !== index,
      collect: (monitor): IManageViewSlotDropState => ({
        highlight: monitor.canDrop() && monitor.isOver(),
      }),
    }),
    [index],
  );

  return (
    <div
      ref={drop}
      className={classNames("slideshow-images-manage-slot", {
        "slideshow-images-manage-slot-highlight": dropState.highlight,
      })}
    >
      {children}
    </div>
  );
}

const ACCEPTED_MANAGE_VIEW_ITEM_DRAG_ITEM = "website-builder-slideshow-images-manage-item";

interface IManageViewItemDragItem {
  type: typeof ACCEPTED_MANAGE_VIEW_ITEM_DRAG_ITEM;
  index: number;
  image: IWebsiteBuilderSlideshowImage;
  width: number;
  height: number;
}

interface IManageViewItemDragState {
  isDragging: boolean;
}

interface IManageViewItemProps {
  image: IWebsiteBuilderSlideshowImage;
  index: number;
  hovered: boolean;
  selected: boolean;
  setHoveredMediaId: React.Dispatch<React.SetStateAction<number>>;
  selectImage: (event: React.MouseEvent<HTMLDivElement>) => void;
  removeImage: () => void;
  replaceImage: () => void;
}

function ManageViewItem(props: IManageViewItemProps) {
  const {
    image,
    index,
    hovered,
    selected,
    setHoveredMediaId,
    selectImage,
    removeImage,
    replaceImage,
  } = props;

  const { t } = useTranslation();

  const dragSurfaceRef = useRef<HTMLDivElement>(null);

  const [dragState, drag, dragPreview] = useDrag<IManageViewItemDragItem, unknown, IManageViewItemDragState>(() => {
    const type = ACCEPTED_MANAGE_VIEW_ITEM_DRAG_ITEM;

    return {
      type,
      item: () => {
        const boundingRect = dragSurfaceRef.current?.getBoundingClientRect();

        setHoveredMediaId(null);

        return {
          type,
          index,
          image,
          width: boundingRect?.width ?? 0,
          height: boundingRect?.height ?? 0,
        };
      },
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, [index, image]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <div
      className={classNames("slideshow-images-manage-item", {
        "slideshow-images-manage-item-dragging": dragState.isDragging,
        "slideshow-images-manage-item-hovered": hovered,
        "slideshow-images-manage-item-selected": selected,
      })}
      onClick={selectImage}
      onMouseLeave={() => setHoveredMediaId(null)}
      onMouseMove={() => setHoveredMediaId(image.mediaId)}
    >
      <div
        ref={node => {
          drag(node);
          dragSurfaceRef.current = node;
        }}
        className="slideshow-images-manage-item-drag-surface"
      >
        <img
          src={image.src}
          alt={image.alt}
          draggable={false}
        />
      </div>
      <div
        className="slideshow-images-manage-item-number"
        onClick={event => event.stopPropagation()}
      >
        {index + 1}
      </div>
      <div
        className="slideshow-images-manage-item-actions"
        onClick={event => event.stopPropagation()}
      >
        <NavigationDropdownNew
          hideChevron
          showPlainTextLabel
          rightAlign
          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
          sections={[
            [
              {
                label: t("Replace"),
                icon: faRotate.iconName,
                onClick: replaceImage,
              },
            ],
            [
              {
                label: t("Remove"),
                icon: faTrashCan.iconName,
                color: "red",
                onClick: removeImage,
              },
            ],
          ]}
        />
      </div>
    </div>
  );
}

function ManageViewDragLayer() {
  const { item, sourceClientOffset } = useDragLayer(monitor => ({
    item: monitor.getItem<IManageViewItemDragItem>(),
    sourceClientOffset: monitor.getSourceClientOffset(),
  }));

  if (
    item?.type !== ACCEPTED_MANAGE_VIEW_ITEM_DRAG_ITEM ||
    sourceClientOffset == null ||
    item.width === 0 ||
    item.height === 0
  ) {
    return null;
  }

  return (
    <Portal parentId={WEBSITE_BUILDER_DRAG_LAYER}>
      <div
        className="website-builder-slideshow-images-manage-drag-layer"
        style={{
          left: sourceClientOffset.x,
          top: sourceClientOffset.y,
          width: item.width,
          height: item.height,
          zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
        }}
      >
        <img
          src={item.image.src}
          alt={item.image.alt}
          draggable={false}
        />
      </div>
    </Portal>
  );
}
